/**
 * 권한 Context
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { MenuPermission } from '../types/menu';
// import { menuService } from '../services/menuService'; // 새로운 메뉴 시스템은 menu_id 기반이므로 사용하지 않음
import { ROLE_SUPER_ADMIN } from '../utils/constants';

interface PermissionContextType {
  permissions: MenuPermission[];
  isLoading: boolean;
  hasPermission: (menuPath: string, permission: 'can_view' | 'can_create' | 'can_update' | 'can_delete') => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context;
};

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<MenuPermission[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadPermissions = async () => {
    if (!user) {
      setPermissions([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      // TODO: 새로운 메뉴 권한 시스템(menu_id 기반)으로 마이그레이션 필요
      // 현재는 기존 권한 시스템(menu_path 기반)을 사용하므로 빈 배열로 설정
      // const menuPermissions = await menuService.getMenuPermissions();
      setPermissions([]);
    } catch (error) {
      console.error('Failed to load permissions:', error);
      setPermissions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPermissions();
  }, [user]);

  const hasPermission = (
    menuPath: string,
    permission: 'can_view' | 'can_create' | 'can_update' | 'can_delete'
  ): boolean => {
    if (!user) return false;

    // 슈퍼관리자는 모든 권한 허용
    // TODO: 백엔드에서 role_group_name을 제공하면 이를 확인
    // if (user.role_group_name === ROLE_SUPER_ADMIN) {
    //   return true;
    // }

    const menuPermission = permissions.find(p => p.menu_path === menuPath);
    if (!menuPermission) {
      // 권한 정보가 없으면 기본적으로 조회 권한만 허용
      return permission === 'can_view';
    }

    return menuPermission[permission] || false;
  };

  const refreshPermissions = async () => {
    await loadPermissions();
  };

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        isLoading,
        hasPermission,
        refreshPermissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

